<template>
  <div class="w-full flex flex-col">
    <div class="w-full flex items-center p-1 bg-neutral-25 mb-4" style="border-radius: 10px;">
      <div class="w-8 h-8 flex items-center justify-center">
        <AdRankIcon class="text-icon-muted" />
      </div>
      <BaseText type="label" size="sm" class="text-text-muted ml-1.5">
        Ad Rank
      </BaseText>
      <BaseText type="body" size="sm" class="text-text-normal ml-3">
        Compare ads by their relative share of total impressions in this brand's Meta library.
      </BaseText>
    </div>
    <AdRankingGraph 
      :page-id="firestoreBrand?.adLibraryId"
      @show-info-modal="showRankInformationModal = true" 
    />
    <div class="w-full grid grid-cols-3 gap-x-1 mt-3 p-0.5 rounded-xl bg-neutral-25" style="height: 88px">
      <div v-for="(category, index) in adRankCategories" :key="`category-${category.key}`" class="w-full flex items-center">
        <div v-if="index > 0" class="mr-1 border-l border-border-normal" style="height: 64px" />
        <BaseTooltipV2
          class="w-full"
          :tooltip-enabled="category.count === 0"
          text="No ads in this category"
          position="cursor"
          offset="6"
          :delay="500"
        >
          <button class="category-atom"
          :class="{
            'enabled': category.count > 0,
            'active': selectedCategory === category.key,
            'first': index === 0,
            'last': index === adRankCategories.length - 1
          }"
          :disabled="category.count === 0"
          @click="handleChangeCategory(category.key)">
            <!-- Active Indicator -->
            <div class="active-indicator"><div class="inner"/></div>
            <!-- Label -->
            <div class="flex items-center gap-1 pl-1 py-1 pr-2 rounded-md z-10" 
            :style="{ color: category.labelColors.text, backgroundColor: category.labelColors.background }">
              <component :is="category.icon" />
              <BaseText type="label" size="sm">
                {{ category.label }}
              </BaseText>
            </div>
            <!-- Counts & Change -->
            <div class="flex items-center z-10">
              <div v-if="loadingInitialFetch" class="skeleton h-6 w-12 rounded-md" />
              <BaseText v-else type="heading" size="sm" class="pl-1 text-text-muted fade-in">
                {{ category.count.toLocaleString() }}
              </BaseText>
              <div v-if="loadingInitialFetch" class="skeleton h-4 w-24 ml-2.5 rounded-md" />
              <BaseText v-else-if="category.periodDiff === 0" type="body" size="xs" class="ml-3.5 text-text-normal fade-in">
                No change since last week
              </BaseText>
              <div v-else class="ml-2.5 flex items-center fade-in">
                <PeriodDiffIcon class="transform text-icon-normal" :style="{ transform: category.periodDiff >= 0 ? 'scaleY(1)' : 'scaleY(-1)' }"/>
                <BaseText type="label" size="xs" class="ml-1 text-text-normal">
                  {{ category.periodDiff >= 0 ? '+' : '' }}{{ category.periodDiff.toLocaleString() }}
                </BaseText>
                <BaseText type="body" size="xs" class="ml-1.5 text-text-normal">
                  since last week
                </BaseText>
              </div>
            </div>
          </button>
        </BaseTooltipV2>
      </div>
    </div>
    <div class="w-full mt-3">
      <AdvertisementList
        v-if="rankedAds.length"
        discovery
        :advertisements="rankedAds"
        :loading="loadingRankedAds"
        :all-ads-loaded="allAdsLoaded"
        hide-creative-count
        show-spyder-ad-rank
        @getMoreAds="getMoreRankedAds"
        @updateAds="updateRankedAdsLocal"
      />
    </div>
    <div v-if="loadingRankedAds && !rankedAds.length" class="flex flex-col items-center gap-2 mt-24">
      <ScrapingLoadingIcon :width="80" :height="80" />
      <BaseText type="label" size="sm" class="text-text-subdued">
        Pulling the latest rankings
      </BaseText>
    </div>
    <infinite-loading
      v-if="rankedAds.length && !allAdsLoaded"
      :identifier="infiniteId"
      @infinite="getMoreRankedAds"
    >
      <div slot="spinner">
        <BaseLoadingLogo :margin="2" />
      </div>
      <div slot="no-more" />
      <div slot="no-results" />
    </infinite-loading>
    <RankInformationModal v-if="showRankInformationModal" @close="showRankInformationModal = false" />
  </div>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import InfiniteLoading from 'vue-infinite-loading'

// Components
import AdRankingGraph from '../adRanking/AdRankingGraph.vue'
import AdvertisementList from '@/components/advertisements/AdvertisementList.vue'
import RankInformationModal from '../adRanking/infoModal/RankInformationModal.vue'

// Icons
import AdRankIcon from '@/components/globals/Icons/SpyderModeIcons/AdRankIcon.vue'
import PeriodDiffIcon from '@/components/globals/Icons/LensIcons/PeriodDiffIcon.vue'
import ScrapingLoadingIcon from '@/components/globals/Icons/SpyderV2Icons/ScrapingLoadingIcon.vue'

// Category
import BreakoutIcon from '@/components/globals/Icons/SpyderV2Icons/BreakoutIcon.vue'
import EvergreenIcon from '@/components/globals/Icons/SpyderV2Icons/EvergreenIcon.vue'
import ScatterGraphIcon from '@/components/globals/Icons/LensIcons/graphs/ScatterGraphIcon.vue'

const ADS_PER_PAGE = 20

export default {
  name: 'AdRankBrandView',
  components: {
    AdRankingGraph,
    AdvertisementList,
    RankInformationModal,
    AdRankIcon,
    PeriodDiffIcon,
    ScrapingLoadingIcon,
    InfiniteLoading
  },
  props: {
    firestoreBrand: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      selectedCategory: 'all',
      showRankInformationModal: false,
      rankedAds: [],
      categoryCounts: {
        current: { all: 0, breakout: 0, evergreen: 0 },
        previous: { all: 0, breakout: 0, evergreen: 0 }
      },
      currentPage: 0,
      loadingInitialFetch: true,
      loadingRankedAds: false,
      allAdsLoaded: false,
      infiniteId: +new Date()
    }
  },
  watch: {
    firestoreBrand: {
      handler(brand) {
        if (!brand) return
        this.fetchRankedAds()
      },
      immediate: true
    }
  },
  computed: {
    adRankCategories () {
      return [
        {
          key: 'all',
          label: 'Active Ads',
          icon: ScatterGraphIcon,
          count: this.categoryCounts.current.all,
          periodDiff: this.categoryCounts.current.all - this.categoryCounts.previous.all,
          labelColors: { text: '#2F3547', background: 'transparent' },
          isActive: this.selectedCategory === 'all'
        },
        {
          key: 'breakout',
          label: 'Breakout',
          icon: BreakoutIcon,
          count: this.categoryCounts.current.breakout,
          periodDiff: this.categoryCounts.current.breakout - this.categoryCounts.previous.breakout,
          labelColors: { text: '#002583', background: '#EEF8FF' },
          isActive: this.selectedCategory === 'breakout'
        },
        {
          key: 'evergreen',
          label: 'Evergreen',
          icon: EvergreenIcon,
          count: this.categoryCounts.current.evergreen,
          periodDiff: this.categoryCounts.current.evergreen - this.categoryCounts.previous.evergreen,
          labelColors: { text: '#224F0B', background: '#F2FEEE' },
          isActive: this.selectedCategory === 'evergreen'
        }
      ]
    }
  },
  methods: {
    async fetchRankedAds (category = 'all', page = 0) {
      if (!this.firestoreBrand?.adLibraryId) return
      this.loadingRankedAds = true
      try {
        const { data } = await ForeplayAPI.Brands.getBrandRankedAds(
          this.firestoreBrand.adLibraryId, 
          category, 
          { limit: ADS_PER_PAGE, page }
        )

        this.rankedAds = [...this.rankedAds, ...data.ads]
        this.categoryCounts = {
          current: data.counts,
          previous: data.previousCounts
        }

        this.allAdsLoaded = data.ads.length < ADS_PER_PAGE
        this.infiniteId++
      } catch (error) {
        console.error('Error fetching ranked ads:', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to fetch ranked ads. See console for details.'
        })
      } finally {
        this.loadingRankedAds = false
        this.loadingInitialFetch = false
      }
    },
    async getMoreRankedAds () {
      if (this.loadingRankedAds) return
      const nextPage = this.currentPage + 1
      this.currentPage = nextPage
      this.fetchRankedAds(this.selectedCategory, nextPage)
    },
    async handleChangeCategory (category) {
      if (this.loadingRankedAds) return
      this.selectedCategory = category
      this.currentPage = 0
      this.rankedAds = []
      this.allAdsLoaded = false
      this.infiniteId++
      this.fetchRankedAds(category)
    },
    updateRankedAdsLocal (adsToUpdate) {
      const duplicateAds = [...this.rankedAds]
      adsToUpdate.forEach((ad) => {
        const index = duplicateAds.findIndex((a) => a.id === ad.id)
        if (index !== -1) {
          duplicateAds[index] = ad
        }
      })
      this.rankedAds = duplicateAds
    }
  }
}
</script>

<style scoped>
.category-atom {
  position: relative;
  height: 84px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border-radius: 10px;
  transition: background-color 200ms ease-in-out, box-shadow 200ms ease-in-out;
  box-shadow: none;
  background-color: transparent;
}
.category-atom:not(.enabled) {
  cursor: default;
}
.category-atom.enabled:hover:not(.active) {
  background-color: #ECEFF3;
}
.category-atom.active {
  background-color: transparent;
  pointer-events: none;
}
.category-atom .active-indicator {
  position: absolute;
  top: -6px;
  bottom: -2px;
  left: -32px;
  right: -32px;
  overflow-y: hidden;
  border-radius: 10px;
  pointer-events: none;
}
.category-atom.first .active-indicator { left: -4px; }
.category-atom.last .active-indicator { right: -4px; }
.category-atom .active-indicator .inner {
  position: absolute;
  top: 6px;
  bottom: 2px;
  left: 28px;
  right: 28px;
  border-radius: 10px;
  background-color: transparent;
  box-shadow: none;
  transition: background-color 150ms ease-in-out, box-shadow 150ms ease-in-out;
}
.category-atom.first .active-indicator .inner { left: 4px; }
.category-atom.last .active-indicator .inner { right: 4px; }
.category-atom.active .active-indicator .inner {
  background-color: white;
  box-shadow: 0 160px 64px 0 rgba(46, 53, 73, 0.02), 0 90px 54px -8px rgba(46, 53, 73, 0.04), 0 40px 40px -12px rgba(46, 53, 73, 0.07), 0 10px 22px -8px rgba(46, 53, 73, 0.16);
}

.fade-in {
  animation: fadeIn 150ms ease-in-out forwards;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>